import * as React from 'react';

import ExclusiveDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/ExclusiveDetailItem';
import LanguagesDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/LanguagesDetailItem';
import LanguagesWithMLInfoDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/LanguagesWithMLInfoDetailItem';
import RecentlyUpdatedDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/RecentlyUpdatedDetailItem';
import ShareableCertificateDetailItem, {
  showShareableCertificate,
} from 'bundles/unified-description-page-common/components/modules/product-overview/details/ShareableCertificateDetailItem';
import StackabilityDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/StackabilityDetailItem';
import SyllabusContentDetailItem from 'bundles/unified-description-page-common/components/modules/product-overview/details/SyllabusContentDetailItem';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import { ProductDetailItemNames } from 'bundles/unified-description-page-common/types/moduleConfig';
import getTotalQuizzesAndAssignments from 'bundles/unified-description-page-common/utils/getTotalQuizzesAndAssignments';
import { isWithinSixMonths } from 'bundles/unified-description-page-common/utils/isWithinSixMonths';

type PageData = Pick<
  ConsumerCoursePageFieldsFragment | EnterpriseCoursePageFieldsFragment,
  | 'launchedAt'
  | 'material'
  | 'primaryLanguages'
  | 'subtitleLanguages'
  | 'activeSpecialization'
  | 'certificates'
  | 'primarySpecialization'
  | 'translatedLanguages'
  | 'dubbedLanguages'
> & { isExclusive?: EnterpriseCoursePageFieldsFragment['isExclusive'] };

const getProductDetailItems = (
  pageData: PageData,
  itemNames: Array<(typeof ProductDetailItemNames)[keyof typeof ProductDetailItemNames]>
) => {
  if (itemNames.length === 0) {
    return [];
  }
  const { totalAssignments, totalQuizzes } = getTotalQuizzesAndAssignments(pageData.material?.weeks || []);

  return itemNames
    .map((itemName) => {
      switch (itemName) {
        case ProductDetailItemNames.ShareableCertificate:
          if (showShareableCertificate(pageData)) {
            return <ShareableCertificateDetailItem key={itemName} />;
          }
          return null;
        case ProductDetailItemNames.RecentlyUpdated:
          // @ts-expect-error DateTime: string
          if (isWithinSixMonths(pageData.launchedAt)) {
            // @ts-expect-error DateTime: string
            return <RecentlyUpdatedDetailItem launchedAt={pageData.launchedAt} key={itemName} />;
          }
          return null;
        case ProductDetailItemNames.SyllabusContent:
          if (!totalAssignments && !totalQuizzes) return null;
          return <SyllabusContentDetailItem totalQuizzes={totalQuizzes} totalAssignments={totalAssignments} />;
        case ProductDetailItemNames.Languages:
          return (
            <LanguagesDetailItem
              primaryLanguages={pageData.primaryLanguages}
              subtitleLanguages={pageData.subtitleLanguages}
            />
          );
        case ProductDetailItemNames.LanguagesWithMLInfo:
          return (
            <LanguagesWithMLInfoDetailItem
              languages={{
                primaryLanguages: pageData.primaryLanguages,
                translatedLanguages: pageData.translatedLanguages,
                subtitleLanguages: pageData.subtitleLanguages || [],
                dubbedLanguages: pageData.dubbedLanguages,
              }}
            />
          );
        case ProductDetailItemNames.Exclusive:
          if (pageData.isExclusive) {
            return <ExclusiveDetailItem />;
          }
          return null;
        case ProductDetailItemNames.Stackability:
          return <StackabilityDetailItem />;
        default:
          return null;
      }
    })
    .filter((item) => item !== null);
};

export default getProductDetailItems;
